import React, { useState } from 'react'
import { ShiftWithDuty, Volunteer, VolunteerWithConflict } from '../data/types'
import { CheckIcon, EyeIcon } from '../icons'
import { Button } from './ui'
import { AllocationTime, Shift } from '../data/types'
import { API_BASE_URL } from '../data'
import { useAuth } from '../routes'
import { useResourcePermissions } from '../hooks/useResourcePermissions'



type TableMeta = {
    idColumn?: string
    nameColumn?: string
    countryColumn?: string
    jamatKhanaColumn?: string
    ServiceRecordColumn?: string
    familyOriginColumn?: string
    phoneColumn?: string
    duplicationsColumn?: string
    statusColumn?: string
    noVolunteersMessage?: string
    viewDuplicationsButton?: (count: number) => string
}

type Props = {
    meta: TableMeta
    data: VolunteerWithConflict[]
    showDuplications?: boolean
    showStatus?: boolean
    showConflicts?: boolean
    selected: VolunteerWithConflict[]
    onSelectionChange: (selected: VolunteerWithConflict[]) => void
    onDuplicationSelect?: (volunteer: VolunteerWithConflict) => void
    onConflictSelect?: (Volunteer: VolunteerWithConflict) => void
    onProfileClick?: (volunteer: VolunteerWithConflict) => void
    onDataRefresh?: (csid: number, newStatus: boolean | null) => void
    lead?: Volunteer
    deputy_lead?: Volunteer
    readOnly?: boolean
    shift: ShiftWithDuty
}

export const ShiftAssignmentVolunteerTable = (props: Props) => {
    const { meta, data, lead, deputy_lead, shift, showDuplications = false, showStatus = false, showConflicts = false, selected, onSelectionChange, onDuplicationSelect, onConflictSelect, onProfileClick, onDataRefresh, readOnly = false } = props

    const volunteerData = data;
    const { user } = useAuth();

    const [updatingVolunteerId, setUpdatingVolunteerId] = useState<number | null>(null);

    const { canChangeAllocationStatus } = useResourcePermissions();

    const isSelected = (volunteer: VolunteerWithConflict) => {
        return selected.some(v => v.csid === volunteer.csid)
    }

    const handleSelectVolunteer = (volunteer: VolunteerWithConflict) => {
        if (isSelected(volunteer)) {
            onSelectionChange(selected.filter(v => v.csid !== volunteer.csid))
        } else {
            onSelectionChange([...selected, volunteer])
        }
    }

    const handleSelectAll = () => {
        if (selected.length === data.length && data.length > 0) {
            // Deselect all
            onSelectionChange([])
        } else {
            // Select all on current page
            onSelectionChange([...data])
        }
    }

    const allSelected = volunteerData.length > 0 && selected.length === volunteerData.length

    const handleAllocationLeadClick = async (volunteer_csid: number, newStatus: boolean | null, allocationTime: AllocationTime) => {

        // Start updating specific volunteer
        setUpdatingVolunteerId(volunteer_csid);

        try {
            const response = await fetch(
                `${API_BASE_URL}/volunteer/${volunteer_csid}/allocation-time/${allocationTime.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        status: newStatus,
                        updated_by_csid: user.volunteer_csid
                    }),
                    credentials: 'include'
                }
            )

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Backend validation error:', errorData);
                throw new Error(errorData.message || 'Failed to update shift status')
            }

            if (onDataRefresh) {
                onDataRefresh(volunteer_csid, newStatus);
            }

        } catch (err) {
            console.error('Error updating allocation status:', err)
        } finally {
            // Stop updating
            setUpdatingVolunteerId(null);
        }
    }

    const LeadCard = ({ title, volunteer, bgColor }: { title: string, volunteer: Volunteer, bgColor: string }) => (
        <div className={`flex items-center justify-between p-4 rounded-lg border ${bgColor} border-opacity-50 dark:border-gray-600 mb-4`}>
            <div className="flex items-center gap-4">
                <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                    {title}
                </span>
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {volunteer.first_name} {volunteer.last_name}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {volunteer.csid}
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-300">
                <div className="hidden md:block">
                    <span className="text-xs text-gray-400 block">Jamatkhana</span>
                    {volunteer.jamat_khana || 'N/A'}
                </div>
                <div className="hidden md:block">
                    <span className="text-xs text-gray-400 block">IV Position</span>
                    {volunteer.service_record || 'N/A'}
                </div>
                <div className="hidden md:block">
                    <span className="text-xs text-gray-400 block">Family Origin</span>
                    {volunteer.family_origin || 'N/A'}
                </div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        onProfileClick && onProfileClick(volunteer as VolunteerWithConflict)
                    }}
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center gap-1"
                >
                    <EyeIcon className="w-4 h-4" />
                    View
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">

            {(lead || deputy_lead) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {lead && (
                        <LeadCard
                            title="Lead"
                            volunteer={lead}
                            bgColor="bg-gray-50 dark:bg-gray-800"
                        />
                    )}
                    {deputy_lead && (
                        <LeadCard
                            title="Deputy Lead"
                            volunteer={deputy_lead}
                            bgColor="bg-gray-50 dark:bg-gray-800"
                        />
                    )}
                </div>
            )}

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">

                {volunteerData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {meta.noVolunteersMessage || 'No volunteers found'}
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                {!readOnly && (
                                    <th className="px-6 py-3 text-start">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                        />
                                    </th>
                                )}
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.idColumn || 'ID'}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.nameColumn || 'Name'}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.phoneColumn || 'Phone Number'}
                                </th>
                                {meta.countryColumn &&
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.countryColumn || 'Country'}
                                    </th>
                                }
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.jamatKhanaColumn || 'Jamatkhana'}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.ServiceRecordColumn || 'IV Position'}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.familyOriginColumn || 'Family Origin'}
                                </th>
                                {showDuplications && (
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.duplicationsColumn || 'Shift Count'}
                                    </th>
                                )}
                                {showConflicts && (
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Conflicts
                                    </th>
                                )}
                                {showStatus && (
                                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {meta.statusColumn || 'Status'}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {volunteerData.map((volunteer) => {
                                const allocationsCount = volunteer.allocations?.filter(
                                    (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks !== false
                                ).length || 0
                                const coreTeamsCount = volunteer.coreTeams?.length || 0
                                const leadPositionsCount = (
                                    (volunteer.eventLeads?.filter((e: any) => e.include_in_duplicate_checks !== false).length || 0) +
                                    (volunteer.areasAsLead?.filter((a: any) => a.event?.include_in_duplicate_checks !== false).length || 0) +
                                    (volunteer.areasAsDeputyLead?.filter((a: any) => a.event?.include_in_duplicate_checks !== false).length || 0) +
                                    (volunteer.shiftsAsLead?.filter((s: any) => s.area?.event?.include_in_duplicate_checks !== false).length || 0) +
                                    (volunteer.shiftsAsDeputyLead?.filter((s: any) => s.area?.event?.include_in_duplicate_checks !== false).length || 0)
                                )
                                const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount
                                const selected = isSelected(volunteer)
                                const isUpdating = updatingVolunteerId === volunteer.csid;

                                return (
                                    <tr
                                        key={volunteer.csid}
                                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${!readOnly ? 'cursor-pointer' : ''} ${selected ? 'bg-primary-50 dark:bg-primary-900' : ''}`}
                                        onClick={!readOnly ? () => handleSelectVolunteer(volunteer) : undefined}
                                    >
                                        {!readOnly && (
                                            <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => handleSelectVolunteer(volunteer)}
                                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                />
                                            </td>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {volunteer.csid}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onProfileClick && onProfileClick(volunteer)
                                                }}
                                                className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline font-medium inline-flex items-center gap-1"
                                            >
                                                <EyeIcon className="w-4 h-4" />
                                                {volunteer.first_name} {volunteer.last_name}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {volunteer.phone || 'N/A'}
                                        </td>
                                        {meta.countryColumn &&
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {volunteer.country || 'N/A'}
                                            </td>
                                        }
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {volunteer.jamat_khana || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {volunteer.service_record || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {volunteer.family_origin || 'N/A'}
                                        </td>
                                        {showDuplications && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {totalCount === 0 ? (
                                                    <span className="text-gray-500 dark:text-gray-400">None</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onDuplicationSelect && onDuplicationSelect(volunteer)
                                                        }}
                                                        className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium text-sm"
                                                    >
                                                        {meta.viewDuplicationsButton ? meta.viewDuplicationsButton(totalCount) : `View ${totalCount}`}
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                        {showConflicts && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onConflictSelect && onConflictSelect(volunteer)
                                                    }}
                                                    className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium text-sm"
                                                >
                                                    {`View (${volunteer.conflicts?.length})`}
                                                </button>
                                            </td>
                                        )}
                                        {showStatus && volunteer.allocation && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {volunteer.allocation.status === null ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200">
                                                        Pending
                                                    </span>
                                                ) : volunteer.allocation.status ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200">
                                                        <CheckIcon className="w-3 h-3 me-1" />
                                                        Accepted
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200">
                                                        Declined
                                                    </span>
                                                )}
                                            </td>
                                        )}

                                        {(canChangeAllocationStatus()) &&
                                            < td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className='flex gap-2'>
                                                    {isUpdating ? (
                                                        <span className='text-xs text-gray-500 italic animate-pulse'>Updating...</span>
                                                    ) : (
                                                        shift?.shift_status === 2 && <>
                                                            {volunteer.allocation.status === null ? (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAllocationLeadClick(volunteer.csid, true, volunteer.allocation);
                                                                        }}
                                                                        variant="solid"
                                                                        color="success"
                                                                        size="sm"
                                                                    >
                                                                        Accept
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAllocationLeadClick(volunteer.csid, false, volunteer.allocation)
                                                                        }}
                                                                        variant="solid"
                                                                        color="danger"
                                                                        size="sm"
                                                                    >
                                                                        Decline
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <Button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAllocationLeadClick(volunteer.csid, null, volunteer.allocation)
                                                                    }}
                                                                    variant="outline"
                                                                    color="warn"
                                                                    size='sm'
                                                                >
                                                                    Reset
                                                                </Button>
                                                            )}
                                                        </>

                                                    )}
                                                </div>
                                            </td>
                                        }
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div >
    )
}